import React, { useState, useEffect } from 'react';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { TbPlus, TbEdit, TbTrash, TbMapPin } from 'react-icons/tb';
import { Auth } from 'aws-amplify';
import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import ActionButton from '../../../atoms/ActionButton';
import ConfirmationModal from '../../../templates/ConfirmationModal';
import useCities from '../../../../hooks/useCities';
import useProvinces from '../../../../hooks/useProvinces';
import streetTypesData from '../../../../resources/street_types.json';

const BASE_URL = `${process.env.REACT_APP_PERSON_API_URL}/individual`;
const API_KEY = process.env.REACT_APP_API_KEY || '';

interface Address {
    id?: string;
    zipcode: string;  // ✅ Corrigido: postalCode -> zipcode
    streetType: string;
    street: string;
    number: string;
    complement: string;
    district: string;
    cityId: string;
    ibgeCode?: string; // ✅ Para matching de cidade via IBGE
}

interface Props {
    individualId: string;
    addresses?: Address[]; // ✅ Opcional
    onAddressesChange?: (addresses: Address[]) => void; // ✅ Opcional
}

const IndividualFormAddresses: React.FC<Props> = ({
    individualId,
    addresses: initialAddresses = [], // ✅ Default vazio
    onAddressesChange = () => {} // ✅ Default função vazia 
}) => {
    const { fetchCities } = useCities();
    const { fetchProvinces } = useProvinces();
    
    const isModalOpen = useSignal<boolean>(false);
    const isDeleteModalOpen = useSignal<boolean>(false);
    const isLoading = useSignal<boolean>(false);
    const provinces = useSignal<any[]>([]);
    const cities = useSignal<any[]>([]);
    const selectedProvince = useSignal<string>('');
    const addresses = useSignal<Address[]>([]); // ✅ Estado interno
    
    const [editingAddress, setEditingAddress] = useState<Address | null>(null);
    const [addressToDelete, setAddressToDelete] = useState<string | null>(null);
    const [form, setForm] = useState<Address>({
        zipcode: '',  // ✅ Corrigido: postalCode -> zipcode
        streetType: 'Rua',
        street: '',
        number: '',
        complement: '',
        district: '',
        cityId: '',
        ibgeCode: '' // ✅ Para matching de cidade
    });

    // ✅ Autofill CEP (seguindo padrão dos Dados Básicos)
    const handleZipcodeChange = async (value: string) => {
        // Remove formatação e mantém apenas números
        value = value.replace(/[^0-9]/g, "");
        setForm(prev => ({ ...prev, zipcode: value }));
        
        // Limpar campos se CEP for apagado
        if (value.length === 0) {
            setForm(prev => ({
                ...prev,
                streetType: 'Rua',
                street: '',
                district: '',
                complement: '',
                cityId: ''
            }));
            selectedProvince.value = '';
            cities.value = [];
            return;
        }
        
        if (value.length === 8) {
            try {
                const response = await fetch(`https://viacep.com.br/ws/${value}/json/`);
                const data = await response.json();
                
                if (data.erro) {
                    console.warn("CEP não encontrado:", value);
                    return;
                }
                
                // ✅ Autofill dos campos de endereço (igual aos Dados Básicos)
                const logradouro = data.logradouro || "";
                const streetTypeName = logradouro.split(" ")[0] || "";
                
                // ✅ Buscar streetType correspondente no resource
                const matchingStreetType = streetTypesData.streetTypes.find(st => 
                    st.name === streetTypeName || st.abbreviations.includes(streetTypeName)
                );
                
                // ✅ Remover o tipo de logradouro do campo street
                const streetWithoutType = matchingStreetType 
                    ? logradouro.replace(streetTypeName, "").trim()
                    : logradouro;
                
                setForm(prev => ({
                    ...prev,
                    streetType: matchingStreetType?.name || streetTypeName || 'Rua',
                    street: streetWithoutType,
                    district: data.bairro || '',
                    complement: data.complemento || '',
                    ibgeCode: data.ibge || '' // ✅ Salvar IBGE para matching
                }));
                
                // ✅ Atualizar estado e carregar cidades (igual aos Dados Básicos)
                if (data.uf) {
                    // ✅ Limpar cidade antes de mudar estado (igual ao handleProvinceChange)
                    setForm(prev => ({ ...prev, cityId: '' }));
                    selectedProvince.value = data.uf;
                    await loadCities(data.uf);
                    
                    // ✅ Aguardar cidades carregarem e selecionar por IBGE (tempo maior)
                    if (data.ibge) {
                        setTimeout(() => {
                            const cityMatch = cities.value.find((city: any) => city.ibgeCode === data.ibge);
                            if (cityMatch) {
                                setForm(prev => ({ ...prev, cityId: cityMatch.value }));
                                console.log("✅ Cidade selecionada automaticamente:", cityMatch.label);
                            } else {
                                console.log("⚠️ Cidade não encontrada pelo IBGE:", data.ibge);
                            }
                        }, 500); // ✅ Tempo maior para aguardar carregamento
                    }
                }
                
                console.log("✅ Autofill por CEP realizado:", data);
            } catch (error) {
                console.error('❌ Erro ao buscar CEP:', error);
            }
        }
    };

    // ✅ Usar resource JSON ao invés de hardcoded
    const streetTypes = streetTypesData.streetTypes.map(st => ({
        value: st.name,
        label: st.name
    }));

    useEffect(() => {
        // ✅ Inicializar com dados iniciais se existirem
        if (initialAddresses && initialAddresses.length > 0) {
            addresses.value = initialAddresses;
        }
        
        loadProvinces();
        
        // ✅ Carregar endereços apenas se individualId existe
        if (individualId) {
            console.log('🔄 Carregando endereços para individual:', individualId);
            loadAddresses();
        }
    }, [individualId]); // ✅ Apenas quando individualId muda

    const loadProvinces = async () => {
        try {
            const provincesData = await fetchProvinces();
            provinces.value = provincesData.map((province: any) => ({
                value: province.initials,
                label: `${province.name} (${province.initials})`
            }));
        } catch (error) {
            console.error('Error loading provinces:', error);
        }
    };

    const loadCities = async (provinceInitials: string) => {
        try {
            const citiesData = await fetchCities(provinceInitials);
            cities.value = citiesData.map((city: any) => ({
                value: city.id,
                label: city.name,
                ibgeCode: city.ibgeCode // ✅ Incluir IBGE code para matching
            }));
        } catch (error) {
            console.error('Error loading cities:', error);
        }
    };

    const handleProvinceChange = (provinceInitials: string) => {
        selectedProvince.value = provinceInitials;
        setForm(prev => ({ ...prev, cityId: '' }));
        if (provinceInitials) {
            loadCities(provinceInitials);
        }
    };

    const openModal = async (address?: Address) => {
        if (address) {
            setEditingAddress(address);
            setForm(address);
            
            // ✅ Carregar estado e cidades para edição (igual aos Dados Básicos)
            if (address.cityId) {
                console.log("🔍 Carregando dados da cidade:", address.cityId);
                try {
                    const url = `${process.env.REACT_APP_GEOLOCATION_API_URL}/cities/${address.cityId}`;
                    console.log("📡 URL da API:", url);
                    console.log("🔑 API Key:", API_KEY ? "Presente" : "Ausente");
                    
                    // Buscar dados da cidade para descobrir o estado
                    const response = await fetch(url, {
                        headers: {
                            'x-api-key': API_KEY || ''
                        }
                    });
                    
                    console.log("📡 Response status:", response.status);
                    
                    if (response.ok) {
                        const city = await response.json();
                        console.log("🏙️ Dados da cidade:", city);
                        
                        if (city && city.province) {
                            const provinceInitials = city.province.initials;
                            console.log("🗺️ Estado encontrado:", provinceInitials);
                            
                            selectedProvince.value = provinceInitials;
                            console.log("🗺️ selectedProvince.value definido:", selectedProvince.value);
                            
                            await loadCities(provinceInitials);
                            console.log("✅ Estado carregado e cidades carregadas");
                        } else {
                            console.log("❌ Cidade não tem dados de província");
                        }
                    } else {
                        const errorText = await response.text();
                        console.log("❌ Erro na API:", response.status, errorText);
                    }
                } catch (error) {
                    console.error("❌ Erro ao buscar dados da cidade:", error);
                }
            }
        } else {
            setEditingAddress(null);
            setForm({
                zipcode: '',  // ✅ Corrigido: postalCode -> zipcode
                streetType: 'Rua',
                street: '',
                number: '',
                complement: '',
                district: '',
                cityId: '',
                ibgeCode: ''
            });
            selectedProvince.value = '';
            cities.value = [];
        }
        isModalOpen.value = true;
    };

    const closeModal = () => {
        isModalOpen.value = false;
        setEditingAddress(null);
        setForm({
            zipcode: '',  // ✅ Corrigido: postalCode -> zipcode
            streetType: 'Rua',
            street: '',
            number: '',
            complement: '',
            district: '',
            cityId: '',
            ibgeCode: ''
        });
        selectedProvince.value = '';
        cities.value = [];
    };

    const handleSubmit = async () => {
        if (!form.zipcode || !form.street || !form.number || !form.district || !form.cityId) {  // ✅ Corrigido: postalCode -> zipcode
            toast.error('Preencha todos os campos obrigatórios');
            return;
        }

        isLoading.value = true;
        try {
            const url = editingAddress 
                ? `${BASE_URL}/${individualId}/addresses/${editingAddress.id}`
                : `${BASE_URL}/${individualId}/addresses`;
            
            const method = editingAddress ? 'PUT' : 'POST';
            
            // Obter JWT token do Amplify
            const session = await Auth.currentSession();
            const token = session.getAccessToken().getJwtToken();
            
            // Mapear campos para o formato esperado pelo backend
            const { cityId, id, ...rest } = form;
            const payload = {
                ...rest,
                city_id: cityId  // Backend espera city_id
            };
            
            console.log('🔍 DEBUG PAYLOAD:', {
                url,
                method,
                payload,
                editingAddress: editingAddress?.id || 'null',
                formId: form.id || 'undefined'
            });
            
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY || '',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success(editingAddress ? 'Endereço atualizado!' : 'Endereço cadastrado!');
                await loadAddresses();
                closeModal();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Erro ao salvar endereço');
            }
        } catch (error) {
            toast.error('Erro ao salvar endereço');
            console.error('Error saving address:', error);
        } finally {
            isLoading.value = false;
        }
    };

    const handleDelete = async () => {
        if (!addressToDelete) return;

        isLoading.value = true;
        try {
            // Obter JWT token do Amplify
            const session = await Auth.currentSession();
            const token = session.getAccessToken().getJwtToken();
            
            const response = await fetch(`${BASE_URL}/${individualId}/addresses/${addressToDelete}`, {
                method: 'DELETE',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                toast.success('Endereço excluído!');
                await loadAddresses();
            } else {
                toast.error('Erro ao excluir endereço');
            }
        } catch (error) {
            toast.error('Erro ao excluir endereço');
            console.error('Error deleting address:', error);
        } finally {
            isLoading.value = false;
            isDeleteModalOpen.value = false;
            setAddressToDelete(null);
        }
    };

    const loadAddresses = async () => {
        if (!individualId) {
            console.log('⚠️ individualId está vazio, não carregando endereços');
            return;
        }
        
        console.log('🔍 Carregando endereços para individualId:', individualId);
        
        try {
            // ✅ Obter token do Amplify
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            console.log('🔑 Token Amplify disponível:', token ? 'SIM' : 'NÃO');
            
            const response = await fetch(`${BASE_URL}/${individualId}/addresses`, {
                headers: {
                    'x-api-key': API_KEY || '',
                    'Authorization': `Bearer ${token}`
                }
            });

            console.log('📡 Response status:', response.status);
            if (response.ok) {
                const data = await response.json();
                console.log('📋 Dados recebidos:', data);
                addresses.value = data.addresses || data || []; // ✅ Flexível na estrutura
                console.log('✅ Endereços carregados:', addresses.value.length);
            } else {
                const errorText = await response.text();
                console.error('❌ Erro na resposta:', response.status, response.statusText, errorText);
            }
        } catch (error) {
            console.error('❌ Error loading addresses:', error);
            // 🔧 TEMPORÁRIO: Se for erro CORS, assumir lista vazia até CORS ser corrigido
            console.log('⚠️ Erro CORS detectado - API funciona mas browser bloqueia resposta');
            console.log('⚠️ Assumindo lista vazia temporariamente até CORS ser corrigido na API');
            addresses.value = [];
        }
    };

    const confirmDelete = (addressId: string) => {
        setAddressToDelete(addressId);
        isDeleteModalOpen.value = true;
    };

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                    <TbMapPin className="text-blue-600" />
                    Endereços
                </h3>
                <button
                    id="btn-add-address"
                    onClick={() => openModal()}
                    className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-700"
                >
                    <TbPlus size={16} />
                    Novo Endereço
                </button>
            </div>

            {/* Lista de Endereços */}
            <div className="bg-white rounded-lg shadow">
                {addresses.value.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <TbMapPin size={48} className="mx-auto mb-4 text-gray-300" />
                        <p>Nenhum endereço cadastrado</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Endereço</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">CEP</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Bairro</th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {addresses.value.map((address, index) => (
                                    <tr key={address.id || index} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div>
                                                <div className="font-medium">
                                                    {address.streetType} {address.street}, {address.number}
                                                </div>
                                                {address.complement && (
                                                    <div className="text-sm text-gray-500">{address.complement}</div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm">{address.zipcode}</td>  {/* ✅ Corrigido: postalCode -> zipcode */}
                                        <td className="px-4 py-3 text-sm">{address.district}</td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    id={`btn-edit-address-${address.id || index}`}
                                                    onClick={() => openModal(address)}
                                                    className="text-blue-600 hover:text-blue-800"
                                                    title="Editar"
                                                >
                                                    <TbEdit size={16} />
                                                </button>
                                                <button
                                                    id={`btn-remove-address-${address.id || index}`}
                                                    onClick={() => confirmDelete(address.id!)}
                                                    className="text-red-600 hover:text-red-800"
                                                    title="Excluir"
                                                >
                                                    <TbTrash size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de Cadastro/Edição */}
            {isModalOpen.value && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold mb-4">
                            {editingAddress ? 'Editar Endereço' : 'Novo Endereço'}
                        </h3>

                        <div className="grid grid-cols-12 gap-4">
                            <TextEdit
                                id="zipcode"  // ✅ Corrigido: postalCode -> zipcode
                                label="CEP *"
                                className="col-span-4"
                                value={form.zipcode}  // ✅ Corrigido: postalCode -> zipcode
                                onChange={(e) => handleZipcodeChange(e.target.value)}  // ✅ Usar autofill
                                placeholder="00000-000"
                            />

                            <SelectTextField
                                id="streetType"
                                label="Tipo *"
                                className="col-span-4"
                                value={form.streetType}
                                onChange={(e) => setForm(prev => ({ ...prev, streetType: e.target.value }))}
                                items={streetTypes}
                            />

                            <TextEdit
                                id="street"
                                label="Logradouro *"
                                className="col-span-8"
                                value={form.street}
                                onChange={(e) => setForm(prev => ({ ...prev, street: e.target.value }))}
                            />

                            <TextEdit
                                id="number"
                                label="Número *"
                                className="col-span-4"
                                value={form.number}
                                onChange={(e) => setForm(prev => ({ ...prev, number: e.target.value }))}
                            />

                            <TextEdit
                                id="complement"
                                label="Complemento"
                                className="col-span-6"
                                value={form.complement}
                                onChange={(e) => setForm(prev => ({ ...prev, complement: e.target.value }))}
                            />

                            <TextEdit
                                id="district"
                                label="Bairro *"
                                className="col-span-6"
                                value={form.district}
                                onChange={(e) => setForm(prev => ({ ...prev, district: e.target.value }))}
                            />

                            <SelectTextField
                                id="province"
                                label="Estado *"
                                className="col-span-6"
                                value={selectedProvince.value}
                                onChange={(e) => handleProvinceChange(e.target.value)}
                                items={provinces.value}
                                placeholder="Selecione o estado"
                            />

                            <SelectTextField
                                id="cityId"
                                label="Cidade *"
                                className="col-span-6"
                                value={form.cityId}
                                onChange={(e) => setForm(prev => ({ ...prev, cityId: e.target.value }))}
                                items={cities.value}
                                placeholder="Selecione a cidade"
                                disabled={!selectedProvince.value}
                            />
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                id="btn-cancel-address"
                                onClick={closeModal}
                                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                id="btn-save-address"
                                onClick={handleSubmit}
                                disabled={isLoading.value}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isLoading.value ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Confirmação de Exclusão */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen.value}
                onCancel={() => {
                    isDeleteModalOpen.value = false;
                    setAddressToDelete(null);
                }}
                onConfirm={handleDelete}
                title="Excluir Endereço"
                id={addressToDelete || ''}
                text="Tem certeza que deseja excluir este endereço? Esta ação não pode ser desfeita."
            />
        </div>
    );
};

export default IndividualFormAddresses;
